import type { Tour } from '../tour-types';

/**
 * A survey's results. They are the Results tab of the survey's own page (`/surveys/:id`),
 * which `/surveys/:id/results` redirects to, so the tour belongs to that tab. Its panels
 * only render once someone has answered; before that no anchor is on screen and the tour
 * waits for a later visit.
 */
export const surveyResultsTour: Tour = {
  key: 'surveys.results',
  version: 3,
  routes: ['/surveys/:id'],
  screen: 'results',
  steps: [
    {
      anchor: 'survey-results-summary',
      placement: 'bottom',
      title: 'Summary',
      body: "The headline score on this survey's scale, how many people answered out of how many saw it, and how many left a comment.",
    },
    {
      // NPS and CSAT only: CES has no bands, so the panel and this step drop out.
      anchor: 'survey-results-distribution',
      placement: 'bottom',
      title: 'Distribution',
      body: 'Answers grouped into bands: promoters, passives and detractors for NPS, satisfied and unsatisfied for CSAT.',
    },
    {
      anchor: 'survey-results-questions',
      placement: 'bottom',
      title: 'By question',
      body: 'How each question was answered: every score on its scale, and every option with its share of the people who answered.',
      since: 2,
    },
    {
      // Only shown when the survey is tied to an experiment, or its visitors were in one.
      anchor: 'survey-results-variants',
      placement: 'bottom',
      title: 'By variant',
      body: 'The same results split by the experiment variant each visitor was in. Small groups are marked, as their differences are mostly noise.',
      since: 3,
    },
    {
      anchor: 'survey-results-timeline',
      placement: 'bottom',
      title: 'Over time',
      body: 'Responses and the score per day or week, so you can see a change land or a trend build.',
      since: 2,
    },
    {
      // Only shown once sentiment analysis is on, or has classified something.
      anchor: 'survey-results-sentiment',
      placement: 'bottom',
      title: 'Sentiment',
      body: 'Written answers classified as positive, neutral or negative. Switch it on or off under Overview; it uses AI credits.',
    },
    {
      anchor: 'survey-results-filters',
      placement: 'bottom',
      title: 'Filter responses',
      body: 'Narrow the list below to positive, neutral or negative comments, or to answers that came with a comment.',
    },
    {
      anchor: 'survey-results-response',
      placement: 'top',
      title: 'One response',
      body: 'Everything one person answered, read together, with when and on what device. They are listed newest first.',
    },
  ],
};
